import { Loading } from '@/components/UI/Loading'
import styles from '@/styles/Scanner.module.css'
import type { BrowserCodeReader, IScannerControls } from '@zxing/browser'
import { BrowserMultiFormatReader, BrowserQRCodeReader } from '@zxing/browser'
import * as Zxing from '@zxing/library'
import type { Exception } from '@zxing/library'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'

/** 読み取り成功後、次のデコードを試みるまでの待機時間（ms）。 */
const SCAN_SUCCESS_DELAY = 1000
/**
 * デコード試行の間隔（ms）。
 * ZXing の既定値は 500ms（毎秒2回）で、手ぶれ・AF合焦中のコマを引くと即失敗するため短くする。
 */
const SCAN_ATTEMPT_INTERVAL = 100
/**
 * デコード用キャンバスの長辺上限（px）。
 * 切り出した領域は原則 1:1 で扱い、これを超える場合のみ縮小してデコードコストを抑える。
 */
const MAX_DECODE_EDGE = 720
/**
 * カメラに要求する映像の幅（px）。
 * 端末既定（多くは 640×480）では QR 1モジュールが 4〜5px しか確保できず手ぶれに耐えられないため、
 * 解像度を引き上げて余裕を作る。読み取り窓の内側しかデコードしないので、
 * 解像度を上げてもデコード対象のピクセル数は全面デコードだった従来より小さい。
 */
const CAMERA_IDEAL_WIDTH = 1280

export type ScanMode = 'qr' | 'barcode'

/** 動画ソース（videoWidth × videoHeight）上の矩形。 */
type SourceRect = { x: number; y: number; w: number; h: number }

/**
 * 画面上の矩形を、動画ソース（videoWidth × videoHeight）の座標へ変換する。
 *
 * `.video` は `object-fit: cover` 相当（表示ボックスを覆う倍率でスケールし中央寄せ）で描画されるため、
 * 表示ボックスとソースの実寸から逆変換する。ソース範囲外へはみ出す分はクランプする。
 * 表示ボックスが画面外へはみ出している場合でも、`getBoundingClientRect()` 由来の値だけで計算するので
 * 「画面に見えている範囲」と切り出し範囲は一致する。
 */
const toSourceRect = (video: HTMLVideoElement, area: DOMRect): SourceRect | null => {
  const box = video.getBoundingClientRect()
  const sourceWidth = video.videoWidth
  const sourceHeight = video.videoHeight
  if (sourceWidth === 0 || sourceHeight === 0 || box.width === 0 || box.height === 0) return null

  const scale = Math.max(box.width / sourceWidth, box.height / sourceHeight)
  // 中央寄せによって表示ボックスの外側へ出ているソースの量（片側）
  const offsetX = (sourceWidth * scale - box.width) / 2
  const offsetY = (sourceHeight * scale - box.height) / 2

  const x = Math.max(0, Math.min((area.left - box.left + offsetX) / scale, sourceWidth))
  const y = Math.max(0, Math.min((area.top - box.top + offsetY) / scale, sourceHeight))
  const w = Math.min(area.width / scale, sourceWidth - x)
  const h = Math.min(area.height / scale, sourceHeight - y)
  if (w <= 0 || h <= 0) return null

  return { x, y, w, h }
}

export interface ScannerProps {
  onRead?: (success: Zxing.Result) => Promise<boolean | undefined>
  ready?: boolean
  guide?: boolean
  /** 読み取りモード: 'qr' = QRコード専用 / 'barcode' = バーコード含む全フォーマット */
  mode?: ScanMode
  isPaused?: boolean
  /** クーポンを読み込む場合かどうか */
  coupon?: boolean
  /** 下部の説明を押下した時の遷移先 */
  href?: string
  /** カメラ許可が得られなかった（キャンセル・拒否）時のコールバック */
  onCameraPermissionDenied?: () => void
}

/**
 * このコンポーネントのstorybookは作成しない。
 * ScanQrOverlayでのstorybookで確認するため。
 * */
const Scanner = React.memo(
  ({
    onRead,
    ready = false,
    guide,
    mode = 'barcode',
    isPaused = false,
    coupon = false,
    href,
    onCameraPermissionDenied,
  }: ScannerProps) => {
    const { t } = useTranslation()
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const readerRef = useRef<BrowserCodeReader | null>(null)
    const controlRef = useRef<IScannerControls | null>(null)
    const [isLoading, setLoading] = useState(true)
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
    // デコード対象（ROI）の矩形を測るための要素。オーバーレイが開けている窓と同じ位置・大きさ。
    const scanAreaRef = useRef<HTMLDivElement | null>(null)
    // camera-permission-denied リスナーの参照（canplay 成功時に削除するため ref 保持）
    const permissionDeniedListenerRef = useRef<(() => void) | null>(null)
    // 最新のコールバック props を ref で保持し、identity 変化でカメラ起動 effect が再実行されないようにする
    const onReadRef = useRef(onRead)
    onReadRef.current = onRead
    const onCameraPermissionDeniedRef = useRef(onCameraPermissionDenied)
    onCameraPermissionDeniedRef.current = onCameraPermissionDenied
    // stopCamera のたびにインクリメントし、await 中（in-flight）の startCamera を無効化する世代カウンタ
    const generationRef = useRef(0)
    // 取得中のカメラストリーム。
    // アンマウント時は React が ref を detach したあとに effect の cleanup が走るため
    // videoRef.current が null になり、srcObject 経由ではトラックを停止できない。
    // video 要素とは独立にストリームを保持し、確実に解放する。
    const streamRef = useRef<MediaStream | null>(null)

    const onScanFailed = useCallback((error: Exception) => {
      // エラー
      if (error.message) console.log('バーコードエラー', error.message)
    }, [])
    const onScanSuccess = useCallback(async (success: Zxing.Result) => {
      if (!success) return
      await onReadRef.current?.(success)
    }, [])

    const stopCamera = useCallback(() => {
      generationRef.current++
      // カメラストリームとデコードプロセスの停止
      if (controlRef.current) {
        controlRef.current.stop()
        controlRef.current = null
      }

      if (readerRef.current) {
        readerRef.current = null
      }

      // トラックの停止は streamRef に集約する。
      // srcObject には startCamera が常に同じストリームを設定しているため二重に止める必要はなく、
      // videoRef.current はアンマウント時に null になるため srcObject 経由では停止できない。
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          track.stop()
        }
        streamRef.current = null
      }

      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.srcObject = null
      }
    }, [])

    /**
     * デコードループを開始する。
     *
     * ZXing の `decodeFromConstraints()` はフレーム全面（`videoWidth × videoHeight`）を
     * キャンバスへ描いてデコードするため、画面に映っていない範囲の QR まで読み取ってしまう。
     * 読み取り窓の内側だけを切り出してデコードするために、ループは自前で持つ。
     */
    const startDecodeLoop = useCallback(
      (
        reader: BrowserCodeReader,
        video: HTMLVideoElement,
        generation: number,
        canvas: HTMLCanvasElement,
        context: CanvasRenderingContext2D,
      ): IScannerControls => {
        let stopped = false
        let nextAttemptAt = 0

        // stopCamera（unmount・pause・再起動）が走った世代のループは即座に降りる
        const isCurrent = () => !stopped && generation === generationRef.current

        const schedule = () => {
          if (!isCurrent()) return
          if (typeof video.requestVideoFrameCallback === 'function') {
            // 実フレームの到着に同期させる（非対応ブラウザは rAF にフォールバック）
            video.requestVideoFrameCallback((now) => tick(now))
          } else {
            requestAnimationFrame((now) => tick(now))
          }
        }

        const tick = (now: number) => {
          if (!isCurrent()) return
          // getBoundingClientRect() はレイアウトを強制するため、
          // デコードを試みるフレームでのみ計測する（毎フレームだと試行間隔の6倍走る）。
          if (now >= nextAttemptAt) {
            const area = scanAreaRef.current?.getBoundingClientRect()
            // ROI を取れないフレーム（メタデータ未到着など）は間隔を進めず次フレームで再試行する
            const roi = area ? toSourceRect(video, area) : null
            if (roi) {
              nextAttemptAt = now + SCAN_ATTEMPT_INTERVAL
              // 切り出しは原則 1:1。長辺が上限を超える場合のみ縮小する
              const shrink = Math.min(1, MAX_DECODE_EDGE / Math.max(roi.w, roi.h))
              const width = Math.max(1, Math.round(roi.w * shrink))
              const height = Math.max(1, Math.round(roi.h * shrink))
              if (canvas.width !== width) canvas.width = width
              if (canvas.height !== height) canvas.height = height
              context.drawImage(video, roi.x, roi.y, roi.w, roi.h, 0, 0, width, height)
              try {
                const result = reader.decodeFromCanvas(canvas)
                nextAttemptAt = now + SCAN_SUCCESS_DELAY
                void onScanSuccess(result)
              } catch (error) {
                // NotFoundException は「まだ写っていない」だけで通常フロー。
                // 毎フレーム記録すると出力が膨れるため、それ以外の例外のみ従来どおり通知する。
                if (!(error instanceof Zxing.NotFoundException)) {
                  onScanFailed(error as Exception)
                }
              }
            }
          }
          schedule()
        }

        schedule()

        // ストリームの停止・video のクリアは stopCamera が行うため、ここではループのみ止める
        return {
          stop: () => {
            stopped = true
          },
        }
      },
      [onScanSuccess, onScanFailed],
    )

    const startCamera = useCallback(async () => {
      // 既存ストリームを必ず止めてから起動し、アクティブなストリームを常に高々1本に保つ
      stopCamera()
      const generation = generationRef.current
      // 前回の camera-permission-denied リスナーが残っていれば削除してから再登録
      if (permissionDeniedListenerRef.current) {
        videoRef.current?.removeEventListener('camera-permission-denied', permissionDeniedListenerRef.current)
      }
      const handlePermissionDenied = () => {
        setLoading(false)
      }
      permissionDeniedListenerRef.current = handlePermissionDenied

      // canplay は { once: true } で登録し、成功時に camera-permission-denied リスナーも削除する
      videoRef.current?.addEventListener(
        'canplay',
        () => {
          setLoading(false)
          // 成功時: camera-permission-denied リスナーを明示的に解除
          if (permissionDeniedListenerRef.current) {
            videoRef.current?.removeEventListener('camera-permission-denied', permissionDeniedListenerRef.current)
            permissionDeniedListenerRef.current = null
          }
        },
        { once: true },
      )
      // 失敗時にローディングを解除するためのカスタムイベントリスナー
      videoRef.current?.addEventListener('camera-permission-denied', handlePermissionDenied, { once: true })

      const hints = new Map<Zxing.DecodeHintType, Zxing.BarcodeFormat[]>()
      const formats = Object.values(Zxing.BarcodeFormat).filter(
        (format): format is Zxing.BarcodeFormat => typeof format === 'number',
      )
      hints.set(Zxing.DecodeHintType.POSSIBLE_FORMATS, formats)

      // デコード間隔は自前のループ（startDecodeLoop）で制御するため、リーダーには渡さない
      const readerType = mode === 'qr' ? new BrowserQRCodeReader() : new BrowserMultiFormatReader(hints)

      readerRef.current = readerType
      if (!videoRef.current) {
        return
      }
      try {
        // デコード用キャンバスはカメラを起動する前に用意する。
        // 2D コンテキストが取れないとデコードできないため、カメラを開く前に失敗させる
        // （起動後に失敗させると、デコード不能なままストリームだけが残り続ける）。
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d', { willReadFrequently: true })
        if (!context) {
          throw new Error('デコード用キャンバスの2Dコンテキストを取得できませんでした')
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            // height は指定しない。width と両方を指定すると 16:9 を要求することになり、
            // 4:3 がネイティブの端末では上下がクロップされて画角が狭くなる。
            // ideal はソフト制約のため、非対応の端末でもエラーにはならず最も近いモードが選ばれる。
            width: { ideal: CAMERA_IDEAL_WIDTH },
          },
        })
        // await 中に stopCamera（unmount・pause・再起動）が呼ばれた場合はこのストリームを破棄する
        // （videoRef.current は unmount 時 null になる）
        if (generation !== generationRef.current || !videoRef.current) {
          for (const track of stream.getTracks()) {
            track.stop()
          }
          return
        }

        // video へ接続する前に保持する。以降の解放はすべて stopCamera に集約される
        streamRef.current = stream
        const video = videoRef.current
        video.srcObject = stream
        try {
          await video.play()
        } catch (playError) {
          // 自動再生ポリシー等で play() が拒否されてもストリームが表示されることはあるため、
          // ここでは起動を中断せずログのみに留める（ZXing の playVideoOnLoadAsync と同じ扱い）。
          console.error('カメラ再生エラー', playError)
        }
        if (generation !== generationRef.current || !videoRef.current) {
          for (const track of stream.getTracks()) {
            track.stop()
          }
          return
        }

        controlRef.current = startDecodeLoop(readerType, video, generation, canvas, context)
      } catch (e) {
        // await 中に stopCamera（unmount・pause・再起動）が呼ばれていた場合、
        // この起動は破棄済みのため後始末（インターバル停止・ダイアログ表示）を行わない
        if (generation !== generationRef.current) return
        // 例外種別に関わらずインターバル停止とローディング解除を行う
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current)
          intervalIdRef.current = null
        }
        videoRef.current?.dispatchEvent(new Event('camera-permission-denied'))
        if (e instanceof DOMException && e.name === 'NotAllowedError') {
          // カメラ許可のキャンセル・拒否: ダイアログ表示コールバックを呼ぶ
          onCameraPermissionDeniedRef.current?.()
        } else {
          console.error('カメラ起動エラー', e)
        }
      }
    }, [mode, stopCamera, startDecodeLoop])

    const restartCamera = useCallback(() => {
      if (videoRef.current && controlRef.current) {
        const video = videoRef.current
        const videoPaused = video.paused || video.readyState !== 4
        const videoFrozen = video.currentTime === 0 || (video.currentTime === video.duration && video.duration !== 0)

        if (videoPaused || videoFrozen) {
          // 既存 control の停止は startCamera 冒頭の stopCamera に委譲する
          startCamera()
        }
      }
    }, [startCamera])

    useEffect(() => {
      if (isPaused) {
        stopCamera()
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current)
          intervalIdRef.current = null
        }
        return
      }

      startCamera()

      if (!intervalIdRef.current) {
        const checkInterval = setInterval(restartCamera, 1000)
        intervalIdRef.current = checkInterval
      }

      return () => {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current)
          intervalIdRef.current = null
        }
        // isPaused の値によらず必ず停止する（unmount 時のカメラ残留を防ぐ）
        stopCamera()
      }
    }, [startCamera, stopCamera, restartCamera, isPaused])

    useLayoutEffect(() => {
      const main = document.querySelector('main')
      if (!main) return

      const prevBackground = main.style.background
      const prevHeight = main.style.height
      const prevPadding = main.style.padding

      main.style.background = '#000'
      main.style.height = 'calc(100vh - 75px)'
      main.style.padding = '0px'

      return () => {
        main.style.background = prevBackground
        main.style.height = prevHeight
        main.style.padding = prevPadding
      }
    }, [])

    const message =
      mode === 'qr'
        ? t('spot_detail.qr.please_scan_the_qr_code', 'QRを読み込んでください')
        : t('spot_detail.qr.please_scan_the_barcode', 'バーコードを読み込んでください')

    return (
      <section className="relative flex h-full items-center justify-center">
        {isLoading && ready && <Loading />}
        <div className={styles.overlay}>
          {/*
            デコード対象（ROI）の実測用。clip-path が開けている窓と同じ矩形を保つこと。
            clip-path は描画のみをクリップしレイアウトには影響しないため、
            .overlay の子として置くことで窓と同じ座標系（幅 101%）で位置が一致する。
          */}
          <div ref={scanAreaRef} className={styles.scan_area} aria-hidden="true" />
          {coupon && href ? (
            <span className={styles.overlay_text}>
              <a href={href}>
                {ready
                  ? t('reward_detail.if_you_cannot_read_qr', 'QRコードを読み込めない方はこちら')
                  : t('spot_detail.qr.wait_a_moment', '少々お待ちください')}
              </a>
            </span>
          ) : (
            <span className={styles.overlay_text}>
              {ready ? message : t('spot_detail.qr.wait_a_moment', '少々お待ちください')}
            </span>
          )}
        </div>
        {guide && <div className={styles.guide} />}
        <video className={styles.video} ref={videoRef} playsInline muted autoPlay />
      </section>
    )
  },
)

Scanner.displayName = 'Scanner'

export default Scanner
